import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { reexecute_run_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import {
  AUTOMATION_RUN_KEY,
  AUTOMATION_RUNS_KEY,
  LATEST_STEP_RUNS_BASE_KEY,
  PROJECT_RUNS_KEY,
  RUN_LATEST_EXECUTION_KEY,
} from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { ExecutionConfigRequest, Run } from '@/types/runs.ts';
import { watchRun } from '@/features/runBrowserNotifications/watchedRuns.ts';
import { notifications } from '@mantine/notifications';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';

export type RerunRequest = {
  config: ExecutionConfigRequest;
  step_feedback?: Record<string, string>;
  rerun_context?: {
    expected_base_execution_id: string | null;
    source_step_run_id?: string;
    parent_step_run_ids?: string[];
  };
};

type useReexecuteProps = {
  runId: string;
  rerunRequest: RerunRequest;
};

const useReexecuteRun = (): UseMutationResult<Run, AxiosError, useReexecuteProps> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ runId, rerunRequest }: useReexecuteProps) => {
      // const oldFeedback = run?.latest_execution?.step_feedback || {};

      const response = await apiClient.put(reexecute_run_url(runId), rerunRequest);

      return response.data;
    },
    onSuccess: (run, { runId }) => {
      watchRun({
        automationId,
        run,
      });

      void Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_RUNS_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: PROJECT_RUNS_KEY }),
        queryClient.invalidateQueries({ queryKey: AUTOMATION_RUN_KEY(automationId, runId) }),
        queryClient.invalidateQueries({
          queryKey: RUN_LATEST_EXECUTION_KEY(automationId, runId),
        }),
        queryClient.invalidateQueries({
          queryKey: LATEST_STEP_RUNS_BASE_KEY(automationId, runId),
        }),
      ]);
    },
    onError: (error, { runId }) => {
      if (![409, 422].includes(error.response?.status ?? 0)) return;
      notifications.hide(`rerun-${runId}`);
      notifications.show({
        id: `rerun-${runId}`,
        title: 'Rerun rejected',
        message: getHttpErrorMessage(error) || 'Refresh the run and make a new selection.',
        color: 'red',
      });
      // Refresh the evidence and eligibility. A rejected selection always
      // requires another user action; never retry it against a new target.
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_RUN_KEY(automationId, runId) }),
        queryClient.invalidateQueries({ queryKey: RUN_LATEST_EXECUTION_KEY(automationId, runId) }),
        queryClient.invalidateQueries({ queryKey: LATEST_STEP_RUNS_BASE_KEY(automationId, runId) }),
      ]);
    },
  });
};

export default useReexecuteRun;
